import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, CheckCircle, Camera, Crown, ExternalLink } from 'lucide-react';
import api from '../api';
import { useProfilo } from '../context/ProfiloContext';
import usePiano, { apriPortaleAbbonamento, avviaUpgrade } from '../hooks/usePiano';
import './CreaServizio.css';

// Pagina di modifica del profilo + dettagli del fornitore.
function ProfiloModifica() {
  const navigate = useNavigate();
  const { profilo, setAvatar } = useProfilo();
  const { isPro, piano_scadenza, haAbbonamento, cadenze = [], stripeAttivo } = usePiano();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [abbonamentoError, setAbbonamentoError] = useState(null);

  const handleAbbonamento = async (azione, cadenza) => {
    setAbbonamentoError(null);
    try {
      if (azione === 'portale') await apriPortaleAbbonamento();
      else await avviaUpgrade(cadenza);
    } catch (e) {
      setAbbonamentoError(e.response?.data?.errore || e.message);
    }
  };
  const scadenzaFmt = piano_scadenza ? new Date(piano_scadenza).toLocaleDateString('it-IT') : null;

  useEffect(() => {
    const carica = async () => {
      try {
        const res = await api.get('/auth/profilo');
        const d = res.data;
        setForm({
          nome_attivita: d.nome_attivita || '',
          descrizione_attivita: d.descrizione_attivita || '',
          citta: d.citta || '',
          provincia: d.provincia || '',
          regione: d.regione || '',
          cap: d.cap || '',
          indirizzo: d.indirizzo || '',
          telefono: d.telefono || '',
          sito_web: d.sito_web || '',
          instagram: d.instagram || '',
          referente: d.referente || '',
          email_contatto: d.email_contatto || '',
          whatsapp: d.whatsapp || '',
          facebook: d.facebook || '',
          partita_iva: d.partita_iva || '',
          orari: d.orari || '',
          anni_esperienza: d.anni_esperienza ?? '',
          zone_coperte: d.zone_coperte || '',
          note: d.note || '',
        });
      } catch (err) {
        setError('Errore nel caricamento del profilo');
      } finally {
        setLoading(false);
      }
    };
    carica();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAvatar(res.data.url); // aggiorna l'avatar ovunque (navbar inclusa)
    } catch (err) {
      setAvatarError(err.response?.data?.error || err.response?.data?.errore || 'Errore nel caricamento dell\'immagine');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setOk(false);
    setSaving(true);
    try {
      await api.put('/auth/profilo', {
        ...form,
        anni_esperienza: form.anni_esperienza === '' ? null : form.anni_esperienza,
      });
      setOk(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.errore || err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="dashboard-page loading"><div className="spinner" />Caricamento...</div>;
  if (!form) return (
    <div className="dashboard-page error">
      <div className="error-container"><AlertCircle size={48} /><h2>{error || 'Errore'}</h2></div>
    </div>
  );

  const field = (name, label, type = 'text', extra = {}) => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <input type={type} id={name} name={name} value={form[name]} onChange={handleChange} {...extra} />
    </div>
  );

  return (
    <div className="admin-page">
      <h1>Il mio profilo</h1>

      <form onSubmit={handleSubmit} className="profile-form servizio-form">
        {error && <div className="error-message">{error}</div>}
        {ok && (
          <div className="status-message success" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
            <CheckCircle size={18} /> Profilo salvato con successo
          </div>
        )}

        <fieldset>
          <legend>Immagine profilo</legend>
          <div className="avatar-edit">
            <img
              src={profilo?.avatar_url || '/default-avatar.svg'}
              alt="Immagine profilo"
              className="avatar-edit-img"
              onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.svg'; }}
            />
            <div className="avatar-edit-actions">
              <input
                ref={fileInputRef}
                type="file"
                id="avatar"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
              >
                <Camera size={18} /> {avatarUploading ? 'Caricamento...' : (profilo?.avatar_url ? 'Cambia immagine' : 'Carica immagine')}
              </button>
              {avatarError && <div className="error-message">{avatarError}</div>}
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Abbonamento</legend>
          <p className="form-hint" style={{ marginTop: 0 }}>
            Piano attuale: <strong>{isPro ? 'Pro' : 'Free'}</strong>
            {isPro && scadenzaFmt ? ` — attivo fino al ${scadenzaFmt}` : ''}
          </p>
          {isPro ? (
            <button type="button" className="btn btn-secondary" onClick={() => handleAbbonamento('portale')}>
              <ExternalLink size={18} /> Gestisci abbonamento su Stripe
            </button>
          ) : stripeAttivo ? (
            <>
              <div className="pro-card-actions">
                {cadenze.includes('mensile') && (
                  <button type="button" className="btn btn-secondary" onClick={() => handleAbbonamento('checkout', 'mensile')}>
                    <Crown size={16} /> Passa a Pro · €15/mese
                  </button>
                )}
                {cadenze.includes('annuale') && (
                  <button type="button" className="btn btn-primary" onClick={() => handleAbbonamento('checkout', 'annuale')}>
                    <Crown size={16} /> Passa a Pro · €150/anno
                  </button>
                )}
              </div>
              {haAbbonamento && (
                <button type="button" className="link-button" style={{ marginTop: '0.75rem' }}
                  onClick={() => handleAbbonamento('portale')}>
                  Gestisci i pagamenti su Stripe
                </button>
              )}
            </>
          ) : (
            <p className="form-hint">Pagamenti non disponibili al momento.</p>
          )}
          {abbonamentoError && <div className="error-message">{abbonamentoError}</div>}
        </fieldset>

        <fieldset>
          <legend>Attività</legend>
          {field('nome_attivita', 'Nome attività')}
          <div className="form-group">
            <label htmlFor="descrizione_attivita">Descrizione (no link/email)</label>
            <textarea id="descrizione_attivita" name="descrizione_attivita" rows="5"
              value={form.descrizione_attivita} onChange={handleChange} />
          </div>
        </fieldset>

        <fieldset>
          <legend>Località</legend>
          <div className="form-row">
            {field('citta', 'Città')}
            {field('provincia', 'Provincia')}
          </div>
          <div className="form-row">
            {field('regione', 'Regione')}
            {field('cap', 'CAP')}
          </div>
          {field('indirizzo', 'Indirizzo')}
          {field('zone_coperte', 'Zone coperte (per servizi mobili: intrattenimento, food)', 'text', { placeholder: 'Es. Piemonte, Lombardia, Veneto, Emilia-Romagna' })}
        </fieldset>

        <fieldset>
          <legend>Contatti</legend>
          {field('referente', 'Nome referente')}
          <div className="form-row">
            {field('email_contatto', 'Email di contatto pubblica', 'email')}
            {field('telefono', 'Telefono', 'tel')}
          </div>
          <div className="form-row">
            {field('whatsapp', 'WhatsApp')}
            {field('instagram', 'Instagram')}
          </div>
          <div className="form-row">
            {field('facebook', 'Facebook')}
            {field('sito_web', 'Sito web')}
          </div>
        </fieldset>

        <fieldset>
          <legend>Dati azienda & operativi</legend>
          <div className="form-row">
            {field('partita_iva', 'Partita IVA')}
            {field('anni_esperienza', 'Anni di esperienza', 'number', { min: 0 })}
          </div>
          {field('orari', 'Orari / disponibilità')}
          <div className="form-group">
            <label htmlFor="note">Note (no link/email)</label>
            <textarea id="note" name="note" rows="3" value={form.note} onChange={handleChange} />
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>Indietro</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <Save size={18} /> {saving ? 'Salvataggio...' : 'Salva profilo'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProfiloModifica;
